import { useEffect, useState } from 'react';
import {
  FiBarChart2, FiClock, FiCheckCircle, FiStar,
  FiAlertCircle, FiInbox,
} from 'react-icons/fi';
import { Spinner } from '../../components/ui/Spinner';
import { Card, CardContent } from '../../components/ui/Card';
import { Alert } from '../../components/ui/Alert';
import { PortalPage } from '../../components/layout/PortalPage';
import { SectionCard } from '../../components/layout/SectionCard';
import { supabase } from '../../lib/supabase';
import { getEstabelecimentoAtualId } from '../../lib/estabelecimento';

const TIPO_LABEL: Record<string, string> = {
  sugestao: 'Sugestão',
  reclamacao: 'Reclamação',
  denuncia: 'Denúncia',
  elogio: 'Elogio',
  solicitacao_informacao: 'Solicitação de informação',
};

const TIPO_COR: Record<string, string> = {
  sugestao: '#3b82f6',
  reclamacao: '#f59e0b',
  denuncia: '#ef4444',
  elogio: '#10b981',
  solicitacao_informacao: '#8b5cf6',
};

interface Indicadores {
  ano: number;
  total: number;
  concluidas: number;
  tempo_medio_resposta_horas: number;
  avaliadas: number;
  media_estrelas: number;
  taxa_resolucao_percent: number;
  distribuicao_por_tipo: Record<string, number>;
}

export function OuvidoriaIndicadoresPage() {
  const anoAtual = new Date().getFullYear();
  const [ano, setAno] = useState(anoAtual);
  const [estab, setEstab] = useState<{ nome: string; cor_primaria: string | null } | null>(null);
  const [dados, setDados] = useState<Indicadores | null>(null);
  const [carregando, setCarregando] = useState(true);
  const [erro, setErro] = useState<string | null>(null);

  useEffect(() => {
    setCarregando(true);
    setErro(null);
    const id = getEstabelecimentoAtualId();
    Promise.all([
      supabase.rpc('ouvidoria_indicadores_publico', { p_estabelecimento_id: id, p_ano: ano }),
      estab ? Promise.resolve({ data: estab, error: null }) : supabase.from('estabelecimentos').select('nome, cor_primaria').eq('id', id).maybeSingle(),
    ])
      .then(([indResp, estabResp]) => {
        if (indResp.error) throw indResp.error;
        setDados(indResp.data as Indicadores);
        if (estabResp.data) setEstab(estabResp.data as { nome: string; cor_primaria: string | null });
      })
      .catch((e) => setErro(e instanceof Error ? e.message : 'Erro'))
      .finally(() => setCarregando(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ano]);

  // Lista de anos: do anoAtual até 4 anos atrás
  const opcoesAno = [anoAtual, anoAtual - 1, anoAtual - 2, anoAtual - 3, anoAtual - 4];

  const distribuicao = dados ? Object.entries(dados.distribuicao_por_tipo).sort((a, b) => b[1] - a[1]) : [];
  const totalDistribuicao = distribuicao.reduce((s, [, q]) => s + q, 0);
  const taxaConcluidas = dados && dados.total > 0 ? Math.round((dados.concluidas / dados.total) * 100) : 0;

  return (
    <PortalPage
      titulo="Transparência da Ouvidoria"
      subtitulo={`${estab?.nome ? estab.nome + ' · ' : ''}Indicadores obrigatórios da Lei 13.460/2017`}
      icone={<FiBarChart2 size={26} />}
      voltarHref="/"
      largura="larga"
      acoes={
        <select
          value={ano}
          onChange={(e) => setAno(parseInt(e.target.value, 10))}
          className="h-9 rounded-md border border-white/20 bg-white/10 px-3 text-sm font-medium text-white focus:outline-none [&>option]:text-slate-900"
        >
          {opcoesAno.map((a) => (
            <option key={a} value={a}>{a}</option>
          ))}
        </select>
      }
    >
      {carregando ? (
        <div className="flex justify-center py-16"><Spinner tamanho="lg" /></div>
      ) : erro ? (
        <Alert variante="erro">{erro}</Alert>
      ) : !dados || dados.total === 0 ? (
        <SectionCard>
          <div className="text-center py-12">
            <div className="w-14 h-14 mx-auto rounded-full bg-slate-100 flex items-center justify-center mb-3">
              <FiInbox className="text-slate-400" size={26} />
            </div>
            <p className="text-sm font-medium text-slate-700">Nenhuma manifestação registrada em {ano}.</p>
          </div>
        </SectionCard>
      ) : (
        <div className="space-y-5">
          {/* KPIs principais */}
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-3">
            <KPI
              icone={<FiBarChart2 size={16} />}
              label="Total no ano"
              valor={dados.total.toString()}
            />
            <KPI
              icone={<FiCheckCircle size={16} />}
              label="Concluídas"
              valor={`${dados.concluidas}`}
              hint={`${taxaConcluidas}% do total`}
            />
            <KPI
              icone={<FiClock size={16} />}
              label="Tempo médio"
              valor={formatarHoras(dados.tempo_medio_resposta_horas)}
              hint="Da abertura à conclusão"
            />
            <KPI
              icone={<FiStar size={16} />}
              label="Nota média"
              valor={dados.avaliadas > 0 ? `${dados.media_estrelas.toFixed(1)}/5` : '—'}
              hint={`${dados.avaliadas} avaliação(ões)`}
            />
            <KPI
              icone={<FiCheckCircle size={16} />}
              label="Taxa de resolução"
              valor={`${dados.taxa_resolucao_percent}%`}
              hint="Marcadas como resolvidas"
            />
          </div>

          {/* Distribuição por tipo */}
          <SectionCard titulo="Manifestações por tipo" icone={<FiBarChart2 size={18} />}>
            {distribuicao.length === 0 ? (
              <p className="text-sm text-slate-500">Sem distribuição disponível.</p>
            ) : (
              <div className="space-y-3">
                {distribuicao.map(([tipo, qtd]) => {
                  const pct = totalDistribuicao > 0 ? (qtd / totalDistribuicao) * 100 : 0;
                  const corBar = TIPO_COR[tipo] ?? '#64748b';
                  return (
                    <div key={tipo}>
                      <div className="flex items-baseline justify-between mb-1 text-sm">
                        <span className="font-medium text-slate-900">{TIPO_LABEL[tipo] ?? tipo}</span>
                        <span className="text-slate-600 tabular-nums">
                          {qtd} <span className="text-slate-400">({pct.toFixed(1)}%)</span>
                        </span>
                      </div>
                      <div className="h-2.5 bg-slate-100 rounded overflow-hidden">
                        <div
                          className="h-full rounded transition-all"
                          style={{ width: `${pct}%`, background: corBar }}
                        />
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </SectionCard>

          <Alert variante="info">
            <strong>Lei 13.460/2017</strong> — Código de Defesa do Usuário do Serviço Público obriga
            a divulgação destes indicadores. Os dados são atualizados em tempo real conforme
            as manifestações são registradas e processadas pela Ouvidoria.
            <FiAlertCircle className="inline ml-1" size={12} />
          </Alert>
        </div>
      )}
    </PortalPage>
  );
}

function KPI({ icone, label, valor, hint }: { icone: React.ReactNode; label: string; valor: string; hint?: string }) {
  return (
    <Card>
      <CardContent className="p-4">
        <div className="flex items-start gap-2">
          <div className="text-slate-400 flex-shrink-0 mt-1">{icone}</div>
          <div className="min-w-0">
            <div className="text-2xl font-bold text-slate-900 tabular-nums">{valor}</div>
            <div className="text-xs text-slate-500 mt-0.5">{label}</div>
            {hint && <div className="text-[10px] text-slate-400 mt-0.5">{hint}</div>}
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

function formatarHoras(h: number): string {
  if (h < 1) return `${Math.round(h * 60)} min`;
  if (h < 24) return `${h.toFixed(1)}h`;
  const dias = h / 24;
  if (dias < 30) return `${dias.toFixed(1)} dias`;
  const meses = dias / 30;
  return `${meses.toFixed(1)} meses`;
}
